// SPDX-License-Identifier: Apache-2.0
// File: src/audio/AudioEngineController.ts
import { FACTORY_PRESETS, type SynthPreset } from '../presets/FactorySoundLibrary';
import type { SynthParamId } from '../ui/theme/PocketGridRetroTheme';
import { ALL_ASSIGNABLE_PARAMS } from '../ui/theme/PocketGridRetroTheme';

export interface SequencerStepState {
  enabled: boolean;
  note: number;
  drumMask: number; // Bitmask 0..63 for the 6 drum pads
  velocity: number;
  motionLocks: Partial<Record<SynthParamId, number>>;
}

export interface TelemetryFrame {
  peakL: number;
  peakR: number;
  vocoderBands: number[];
  scope: number[];
}

type TelemetryListener = (frame: TelemetryFrame) => void;

class AudioEngineController {
  private ctx: AudioContext | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private initPromise: Promise<void> | null = null;
  private isInitialized = false;
  private telemetryListeners = new Set<TelemetryListener>();
  private params: Record<string, number>;
  private activeRatios: number[] = [...FACTORY_PRESETS[0].opRatios];
  private activeLevels: number[] = [...FACTORY_PRESETS[0].opLevels];

  constructor() {
    this.params = {} as Record<string, number>;
    (Object.keys(ALL_ASSIGNABLE_PARAMS) as SynthParamId[]).forEach((key) => {
      this.params[key] = ALL_ASSIGNABLE_PARAMS[key].defaultValue;
    });
    // Merge default preset params
    Object.entries(FACTORY_PRESETS[0].params).forEach(([k, v]) => {
      if (typeof v === 'number') {
        this.params[k] = v;
      }
    });
  }

  async ensureInitialized(): Promise<void> {
    if (this.isInitialized && this.ctx) {
      if (this.ctx.state === 'suspended') {
        await this.ctx.resume();
      }
      return;
    }

    if (this.initPromise) {
      await this.initPromise;
      if (this.ctx && this.ctx.state === 'suspended') {
        await this.ctx.resume();
      }
      return;
    }

    this.initPromise = (async () => {
      const AudioCtx =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.ctx = new AudioCtx({ latencyHint: 'interactive', sampleRate: 48000 });

      try {
        // Cache-busting query string so live HMR always loads the latest DSP processor
        await this.ctx.audioWorklet.addModule(`/worklets/pocketgrid-dsp-processor.js?v=${Date.now()}`);
        this.workletNode = new AudioWorkletNode(this.ctx, 'pocketgrid-dsp-processor', {
          numberOfInputs: 1,
          numberOfOutputs: 1,
          outputChannelCount: [2],
        });
        this.workletNode.connect(this.ctx.destination);

        // Immediately sync current preset operators & all slider parameters to the worklet
        this.workletNode.port.postMessage({
          type: 'LOAD_PATCH_OPS',
          ratios: this.activeRatios,
          levels: this.activeLevels,
          params: this.params,
        });

        this.workletNode.port.onmessage = (e) => {
          if (e.data && e.data.type === 'TELEMETRY') {
            for (const listener of this.telemetryListeners) {
              listener(e.data as TelemetryFrame);
            }
          }
        };
      } catch (err) {
        console.warn('AudioWorklet initialization error:', err);
      }

      this.isInitialized = true;
      if (this.ctx.state === 'suspended') {
        await this.ctx.resume();
      }
    })();

    await this.initPromise;
  }

  onTelemetry(listener: TelemetryListener): () => void {
    this.telemetryListeners.add(listener);
    return () => this.telemetryListeners.delete(listener);
  }

  async noteOn(
    note: number,
    velocity = 0.85,
    pointerId = note,
    zoneId = 1,
    engineId = 0,
    customRatios?: number[],
    customLevels?: number[],
    customParams?: Partial<Record<SynthParamId, number>>
  ): Promise<void> {
    await this.ensureInitialized();
    this.workletNode?.port.postMessage({
      type: 'NOTE_ON',
      note,
      velocity,
      pointerId,
      zoneId,
      engineId,
      ratios: customRatios,
      levels: customLevels,
      params: customParams,
    });
  }

  noteOff(note: number, pointerId = note): void {
    this.workletNode?.port.postMessage({
      type: 'NOTE_OFF',
      note,
      pointerId,
    });
  }

  async triggerDrum(drumIndex: number, velocity = 0.95): Promise<void> {
    await this.ensureInitialized();
    this.workletNode?.port.postMessage({
      type: 'DRUM_TRIG',
      drumIndex,
      velocity,
    });
  }

  async triggerMetronome(isAccent: boolean, velocity = 0.78): Promise<void> {
    await this.ensureInitialized();
    this.workletNode?.port.postMessage({
      type: 'METRO_CLICK',
      isAccent,
      velocity,
    });
  }

  sendMpeExpression(pointerId: number, pitchDeltaSemis: number, timbreY: number): void {
    this.workletNode?.port.postMessage({
      type: 'MPE_EXPRESSION',
      pointerId,
      pitchDeltaSemis,
      timbreY,
    });
  }

  sendGlobalPitchBend(semitones: number): void {
    this.workletNode?.port.postMessage({
      type: 'PITCH_BEND',
      semitones,
    });
  }

  setParameter(paramId: SynthParamId | string, normalizedValue: number): void {
    const clamped = Math.max(0.0, Math.min(1.0, normalizedValue));
    this.params[paramId] = clamped;
    if (!this.isInitialized) {
      void this.ensureInitialized();
    }
    this.workletNode?.port.postMessage({
      type: 'PARAM_CHANGE',
      paramId,
      value: clamped,
    });
  }

  getParameters(): Record<string, number> {
    return { ...this.params };
  }

  setHoldPedal(enabled: boolean): void {
    this.workletNode?.port.postMessage({
      type: 'SET_HOLD',
      enabled,
    });
  }

  async loadPreset(preset: SynthPreset): Promise<void> {
    this.activeRatios = [...preset.opRatios];
    this.activeLevels = [...preset.opLevels];
    const mergedParams: Record<string, number> = { ...this.params };
    Object.entries(preset.params).forEach(([k, v]) => {
      if (v !== undefined) {
        mergedParams[k] = v;
      }
    });
    this.params = mergedParams;
    await this.ensureInitialized();
    this.workletNode?.port.postMessage({
      type: 'LOAD_PATCH_OPS',
      ratios: this.activeRatios,
      levels: this.activeLevels,
      params: mergedParams,
    });
  }

  async setZonePreset(zoneId: number, preset: SynthPreset): Promise<void> {
    await this.ensureInitialized();
    this.workletNode?.port.postMessage({
      type: 'SET_ZONE_PRESET',
      zoneId,
      ratios: [...preset.opRatios],
      levels: [...preset.opLevels],
      engineId: preset.targetEngineId,
      params: { ...preset.params },
    });
  }

  panicAllNotesOff(): void {
    this.workletNode?.port.postMessage({ type: 'PANIC' });
  }
}

export const synthEngine = new AudioEngineController();
